import { generateAIText } from "@/lib/ai-text";
import type { ProviderType } from "@/ai/providers";

export type PolicyAIAction =
  | "rewrite"
  | "clarify"
  | "formal"
  | "shorten"
  | "expand"
  | "grammar"
  | "summarize"
  | "improve"
  | "missing_section"
  | "implementation_steps"
  | "find_gaps"
  | "custom";

export interface PolicyEditorAIContext {
  title: string;
  department: string;
  companyName?: string;
  programName?: string;
  framework?: string;
  linkedControls?: string[];
}

interface GeneratePolicyAIEditOptions {
  action: PolicyAIAction;
  provider: ProviderType;
  model: string;
  policyContext: PolicyEditorAIContext;
  documentText: string;
  selectedText?: string;
  customInstruction?: string;
}

const ACTION_INSTRUCTIONS: Record<PolicyAIAction, string> = {
  rewrite: "Rewrite the selected policy text while preserving its meaning.",
  clarify: "Make the selected policy text clearer and easier to understand without making it informal.",
  formal: "Make the selected policy text more formal, precise, and suitable for an auditable company policy.",
  shorten: "Shorten the selected policy text while keeping the requirements intact.",
  expand: "Expand the selected policy text with useful policy detail, responsibilities, and compliance-safe wording.",
  grammar: "Fix grammar, spelling, punctuation, and awkward wording in the selected policy text.",
  summarize: "Summarize the selected policy text into concise policy-ready wording.",
  improve: "Improve the full policy for clarity, completeness, structure, and audit readiness.",
  missing_section: "Draft a missing section that would strengthen this policy. Use a clear heading and policy-ready body text.",
  implementation_steps: "Add practical implementation steps, ownership, review cadence, and evidence expectations for this policy.",
  find_gaps: "Identify gaps in the policy and rewrite the content with the gaps addressed. Do not produce commentary.",
  custom: "Follow the user's instruction exactly while keeping the result suitable for a corporate policy.",
};

const stripCodeFence = (value: string) =>
  value
    .replace(/^```(?:html)?\s*/i, "")
    .replace(/```\s*$/i, "")
    .trim();

export async function generatePolicyAIEdit({
  action,
  provider,
  model,
  policyContext,
  documentText,
  selectedText,
  customInstruction,
}: GeneratePolicyAIEditOptions): Promise<string> {
  const target = selectedText?.trim() ? "selected text" : "full document";
  const linkedControls = policyContext.linkedControls?.length
    ? policyContext.linkedControls.join(", ")
    : "None provided";

  const system = `You are an expert corporate policy editor for ManagEaze.
Return valid, clean HTML only. Do not wrap the answer in markdown fences. Do not include explanations, notes, or chat preambles.
Use semantic HTML tags such as h2, h3, p, ul, ol, li, strong, and em.
Do not include html, head, body, style, script, or unsafe attributes.
Preserve compliance meaning and avoid inventing legal citations unless the provided context includes them.`;

  const user = `Policy context:
- Title: ${policyContext.title || "Untitled Policy"}
- Department: ${policyContext.department || "Unassigned"}
- Company: ${policyContext.companyName || "Not provided"}
- Compliance program: ${policyContext.programName || "None"}
- Framework: ${policyContext.framework || "None"}
- Linked controls: ${linkedControls}

Task:
${customInstruction?.trim() || ACTION_INSTRUCTIONS[action]}

Target: ${target}

Selected text:
${selectedText?.trim() || "No selected text. Work from the full document."}

Full document text:
${documentText.trim() || "The document is currently empty."}

Return only the replacement HTML for the ${target}.`;

  const result = await generateAIText({
    provider,
    model,
    system,
    messages: [{ role: "user", content: user }],
    temperature: action === "grammar" ? 0.1 : 0.25,
    maxOutputTokens: selectedText?.trim() ? 2500 : 7000,
  });

  return stripCodeFence(result);
}
